import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StarRating from '../../components/ui/StarRating';
import { supabase } from '../../lib/supabase';
import useTranslation from '../../hooks/useTranslation';
import { PROFESSIONS } from '../../constants/directoryOptions';
import { getCurrentOrgId } from '../../lib/getCurrentOrgId';
import subcontractorService from '../../services/subcontractorService';
import organizationTradesService from '../../services/organizationTradesService';
import SubcontractorCard, { subcontractorStatusCode } from './SubcontractorCard';

// ─── Expiry helper ────────────────────────────────────────────────────────────
function getExpiryStatus(dateStr) {
  if (!dateStr) return { label: '—', colorClass: '', days: null };
  const today = new Date();
  today?.setHours(0, 0, 0, 0);
  const expiry = new Date(dateStr);
  expiry?.setHours(0, 0, 0, 0);
  const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (days <= 0) return { label: 'Expiré', colorClass: 'bg-red-100 text-red-700', days };
  if (days <= 7) return { label: `Expire dans ${days}j`, colorClass: 'bg-orange-100 text-orange-700', days };
  if (days <= 30) return { label: `Expire dans ${days}j`, colorClass: 'bg-yellow-100 text-yellow-700', days };
  return { label: '✓ À jour', colorClass: 'bg-emerald-50 text-emerald-700', days };
}

function getMostUrgentExpiry(insuranceExpiry, rbqExpiry) {
  const a = getExpiryStatus(insuranceExpiry);
  const b = getExpiryStatus(rbqExpiry);
  if (a?.days === null && b?.days === null) return null;
  if (a?.days === null) return b;
  if (b?.days === null) return a;
  return a?.days <= b?.days ? a : b;
}

// subcontractorService renvoie des cles camelCase ; les lignes de la liste sont
// en snake_case, comme la base. Toutes les cles renvoyees sont converties.
const toSnakeCase = (obj) => {
  const out = {};
  for (const key in obj || {}) {
    out[key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`)] = obj?.[key];
  }
  return out;
};

const mapContactRows = (contacts) =>
  (contacts || [])?.map((c) => ({
    id: c?.id,
    role: c?.role || 'Autre',
    name: c?.name || '',
    email: c?.email || '',
    phone: c?.phone || '',
    _delete: false,
  }));

// ─── Valeur en lecture (fenetre de consultation) ──────────────────────────────
const ReadValue = ({ value, multiline = false, className = 'text-xs text-gray-900' }) => (
  <p className={`${className} py-2 break-words ${multiline ? 'whitespace-pre-wrap' : ''}`}>{value || '—'}</p>
);

// Meme regle que dans OnSiteBid : quelque chose, un @, un point ; sans espaces.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const cleanEmail = (value) => String(value || '')?.replace(/\s+/g, '');

const Directory = () => {
  const { t } = useTranslation();

  // Active type: 'subcontractor' | 'client' | 'professional'
  const [activeType, setActiveType] = useState('subcontractor');

  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  // 'view' = fiche en lecture (clic sur une ligne) ; 'edit' = formulaire.
  const [modalMode, setModalMode] = useState('edit');
  const isView = modalMode === 'view' && !!editingRecord;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'company_name', direction: 'asc' });
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [toast, setToast] = useState(null);

  // ── Dynamic trades from organization_trades table ───────────────────────────
  const [orgTrades, setOrgTrades] = useState([]);
  // Nom affiche d'un metier (display_name), quand la compagnie en a defini un.
  const [tradeLabels, setTradeLabels] = useState({});

  useEffect(() => {
    organizationTradesService?.getByOrg()?.then((trades) => {
      setOrgTrades(trades?.map((t) => t?.name) || []);
      const labels = {};
      for (const trade of trades || []) {
        const shown = String(trade?.displayName || '')?.trim();
        if (shown) labels[trade?.name] = shown;
      }
      setTradeLabels(labels);
    });
  }, []);

  // ── Subcontractor-specific state (new 5-section modal) ──────────────────────
  const initialSubFormData = {
    company_name: '',
    trade: '',
    address: '',
    notes: '',
    contactPerson: '',
    phone: '',
    email: '',
    rating: 0,
    insurance_expiry: '',
    rbq_number: '',
    rbq_expiry: '',
    insurance_doc_url: '',
    rbq_doc_url: '',
    // Fiche partagee avec OnSiteBid : statut, contact estimation, NEQ.
    status: 'Active',
    estName: '',
    estEmail: '',
    estPhone: '',
    tax_id: '',
  };

  const [subFormData, setSubFormData] = useState(initialSubFormData);
  // Problemes de saisie de la fiche sous-traitant, par champ.
  const [subFormErrors, setSubFormErrors] = useState({});
  const [insuranceSignedUrl, setInsuranceSignedUrl] = useState(null);
  const [rbqSignedUrl, setRbqSignedUrl] = useState(null);
  const [uploadingInsurance, setUploadingInsurance] = useState(false);
  const [uploadingRbq, setUploadingRbq] = useState(false);
  const [additionalContacts, setAdditionalContacts] = useState([]);
  // Contacts additionnels tels qu'enregistres : retablis par « Annuler ».
  const savedContactsRef = useRef([]);

  const getTableName = (type) => {
    if (type === 'client') return 'clients';
    if (type === 'professional') return 'professionals';
    return 'subcontractors';
  };

  const getInitialFormData = (type) => {
    if (type === 'client') {
      return { company_name: '', contact_person: '', phone: '', email: '', address: '', notes: '', rating: null };
    }
    if (type === 'professional') {
      return { company_name: '', profession: '', contact_person: '', phone: '', email: '', address: '', notes: '', rating: null };
    }
    return { company_name: '', trade: '', contact_person: '', phone: '', email: '', rating: null };
  };

  const [formData, setFormData] = useState(getInitialFormData('subcontractor'));

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Fetch organization_id once on mount
  useEffect(() => {
    const fetchOrgId = async () => {
      const id = await getCurrentOrgId();
      setOrgId(id);
    };
    fetchOrgId();
  }, []);

  // Fetch records when activeType changes
  useEffect(() => {
    fetchRecords(activeType);
    setSearchQuery('');
    setFilterCategory('');
    setSortConfig({ key: 'company_name', direction: 'asc' });
  }, [activeType]);

  const fetchRecords = async (type) => {
    setLoading(true);
    const tableName = getTableName(type);
    const { data, error } = await supabase?.from(tableName)?.select('*')?.order('created_at', { ascending: false });
    if (error) {
      console.error(`[DIRECTORY FETCH ERROR - ${tableName}]`, error);
      setRecords([]);
    } else {
      setRecords(data || []);
    }
    setLoading(false);
  };

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [searchQuery, filterCategory, records, activeType]);

  const applyFilters = () => {
    let filtered = [...records];
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(
        (r) =>
          r?.company_name?.toLowerCase()?.includes(query) ||
          r?.contact_person?.toLowerCase()?.includes(query) ||
          r?.contact_email?.toLowerCase()?.includes(query) ||
          r?.contact_phone?.includes(query)
      );
    }
    if (filterCategory) {
      if (activeType === 'subcontractor') {
        filtered = filtered?.filter((r) => r?.trade === filterCategory);
      } else if (activeType === 'professional') {
        filtered = filtered?.filter((r) => r?.profession === filterCategory);
      }
    }
    setFilteredRecords(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedRecords = [...filteredRecords]?.sort((a, b) => {
    let aValue = (a?.[sortConfig?.key] || '')?.toString()?.toLowerCase();
    let bValue = (b?.[sortConfig?.key] || '')?.toString()?.toLowerCase();
    if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // ── Modal open/close ────────────────────────────────────────────────────────
  const handleAddNew = () => {
    setEditingRecord(null);
    setModalMode('edit');
    setSubFormErrors({});
    if (activeType === 'subcontractor') {
      setSubFormData(initialSubFormData);
      setAdditionalContacts([]);
      setInsuranceSignedUrl(null);
      setRbqSignedUrl(null);
    } else {
      setFormData(getInitialFormData(activeType));
    }
    setShowAddModal(true);
  };

  // Remplit le formulaire avec les valeurs enregistrees d'une fiche.
  const fillFormFromRecord = (record) => {
    setSubFormErrors({});
    if (activeType === 'subcontractor') {
      setSubFormData({
        status: subcontractorStatusCode(record?.status),
        estName: record?.estimating_contact_name || '',
        estEmail: record?.estimating_contact_email || '',
        estPhone: record?.estimating_contact_phone || '',
        tax_id: record?.tax_id || '',
        company_name: record?.company_name || '',
        trade: record?.trade || '',
        address: record?.address || '',
        notes: record?.notes || '',
        contactPerson: record?.contact_person || '',
        phone: record?.contact_phone || '',
        email: record?.contact_email || '',
        rating: record?.rating || 0,
        insurance_expiry: record?.insurance_expiry || '',
        rbq_number: record?.rbq_number || '',
        rbq_expiry: record?.rbq_expiry || '',
        insurance_doc_url: record?.insurance_doc_url || '',
        rbq_doc_url: record?.rbq_doc_url || '',
      });
    } else if (activeType === 'client') {
      setFormData({
        company_name: record?.company_name || '',
        contact_person: record?.contact_person || '',
        phone: record?.contact_phone || '',
        email: record?.contact_email || '',
        address: record?.address || '',
        notes: record?.notes || '',
        rating: record?.rating ?? null,
      });
    } else if (activeType === 'professional') {
      setFormData({
        company_name: record?.company_name || '',
        profession: record?.profession || '',
        contact_person: record?.contact_person || '',
        phone: record?.contact_phone || '',
        email: record?.contact_email || '',
        address: record?.address || '',
        notes: record?.notes || '',
        rating: record?.rating ?? null,
      });
    }
  };

  // mode 'edit' : menu « ⋮ → Modifier ». mode 'view' : clic sur une ligne.
  const handleEdit = async (e, record, mode = 'edit') => {
    e?.stopPropagation();
    setOpenDropdownId(null);
    setEditingRecord(record);
    setModalMode(mode);
    fillFormFromRecord(record);

    if (activeType === 'subcontractor') {
      // Load signed URLs for existing docs
      setInsuranceSignedUrl(null);
      setRbqSignedUrl(null);
      if (record?.insurance_doc_url) {
        const url = await subcontractorService?.getSignedUrl(record?.insurance_doc_url);
        setInsuranceSignedUrl(url);
      }
      if (record?.rbq_doc_url) {
        const url = await subcontractorService?.getSignedUrl(record?.rbq_doc_url);
        setRbqSignedUrl(url);
      }

      // Load additional contacts
      const contacts = mapContactRows(await subcontractorService?.listContacts(record?.id));
      savedContactsRef.current = contacts;
      setAdditionalContacts(contacts);
    }

    setShowAddModal(true);
  };

  const handleView = (record) => handleEdit(null, record, 'view');

  // « Annuler » sur une fiche existante : les champs reprennent les valeurs
  // enregistrees et la fenetre repasse en lecture. Un document televerse ou
  // retire l'est deja (comme avant) : il n'est pas defait.
  const handleCancelEdit = () => {
    const current = records?.find((r) => r?.id === editingRecord?.id) || editingRecord;
    fillFormFromRecord(current);
    setAdditionalContacts(savedContactsRef.current);
    setModalMode('view');
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingRecord(null);
    setModalMode('edit');
    setSubFormErrors({});
    setSubFormData(initialSubFormData);
    setAdditionalContacts([]);
    setInsuranceSignedUrl(null);
    setRbqSignedUrl(null);
    setFormData(getInitialFormData(activeType));
  };

  // Echap ferme la fiche en lecture seulement ; en edition, rien ne change.
  useEffect(() => {
    if (!showAddModal || !isView) return undefined;
    const onKeyDown = (e) => {
      if (e?.key === 'Escape') handleCloseModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showAddModal, isView, activeType]);

  const handleDeleteClick = (e, id) => {
    e?.stopPropagation();
    setOpenDropdownId(null);
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    const tableName = getTableName(activeType);
    const { error } = await supabase?.from(tableName)?.delete()?.eq('id', deleteConfirmId);
    if (error) {
      console.error(`[DIRECTORY DELETE ERROR - ${tableName}]`, error);
    } else {
      setRecords((prev) => prev?.filter((r) => r?.id !== deleteConfirmId));
    }
    setDeleteConfirmId(null);
  };

  // ── Document upload handlers (subcontractor only) ───────────────────────────
  const handleDocUpload = async (type, file) => {
    const allowedMimes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedMimes?.includes(file?.type)) {
      showToast(t('directory.modal.svgNotSupported') || 'SVG non supporté (PDF, JPG, PNG seulement)');
      return;
    }
    if (file?.size > 20 * 1024 * 1024) {
      showToast(t('directory.modal.fileTooLarge') || 'Fichier trop volumineux (max 20 Mo)');
      return;
    }

    if (editingRecord?.id) {
      const currentOrgId = orgId || (await getCurrentOrgId());
      if (type === 'insurance') setUploadingInsurance(true);
      else setUploadingRbq(true);

      try {
        const { path, signedUrl } = await subcontractorService?.uploadDocument(
          currentOrgId, editingRecord?.id, type, file
        );
        if (type === 'insurance') {
          setSubFormData((prev) => ({ ...prev, insurance_doc_url: path }));
          setInsuranceSignedUrl(signedUrl);
          setRecords((prev) =>
            prev?.map((r) => (r?.id === editingRecord?.id ? { ...r, insurance_doc_url: path } : r))
          );
        } else {
          setSubFormData((prev) => ({ ...prev, rbq_doc_url: path }));
          setRbqSignedUrl(signedUrl);
          setRecords((prev) =>
            prev?.map((r) => (r?.id === editingRecord?.id ? { ...r, rbq_doc_url: path } : r))
          );
        }
      } catch (err) {
        showToast('Erreur lors du téléversement');
        console.error(err);
      } finally {
        if (type === 'insurance') setUploadingInsurance(false);
        else setUploadingRbq(false);
      }
    } else {
      // New subcontractor: store file in state, upload after creation
      if (type === 'insurance') {
        setSubFormData((prev) => ({ ...prev, _insuranceFile: file, insurance_doc_url: file?.name }));
      } else {
        setSubFormData((prev) => ({ ...prev, _rbqFile: file, rbq_doc_url: file?.name }));
      }
    }
  };

  const handleDocRemove = async (type) => {
    if (editingRecord?.id) {
      const currentOrgId = orgId || (await getCurrentOrgId());
      try {
        await subcontractorService?.removeDocument(currentOrgId, editingRecord?.id, type);
        if (type === 'insurance') {
          setSubFormData((prev) => ({ ...prev, insurance_doc_url: '' }));
          setInsuranceSignedUrl(null);
          setRecords((prev) =>
            prev?.map((r) => (r?.id === editingRecord?.id ? { ...r, insurance_doc_url: null } : r))
          );
        } else {
          setSubFormData((prev) => ({ ...prev, rbq_doc_url: '' }));
          setRbqSignedUrl(null);
          setRecords((prev) =>
            prev?.map((r) => (r?.id === editingRecord?.id ? { ...r, rbq_doc_url: null } : r))
          );
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      if (type === 'insurance') {
        setSubFormData((prev) => ({ ...prev, _insuranceFile: null, insurance_doc_url: '' }));
      } else {
        setSubFormData((prev) => ({ ...prev, _rbqFile: null, rbq_doc_url: '' }));
      }
    }
  };

  // ── Additional contacts handlers ────────────────────────────────────────────
  const addContactRow = () => {
    setAdditionalContacts((prev) => [
      ...prev,
      { id: null, role: 'Autre', name: '', email: '', phone: '', _delete: false },
    ]);
  };

  const updateContactRow = (index, field, value) => {
    setAdditionalContacts((prev) =>
      prev?.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const removeContactRow = (index) => {
    setAdditionalContacts((prev) => {
      const contact = prev?.[index];
      if (contact?.id) {
        return prev?.map((c, i) => (i === index ? { ...c, _delete: true } : c));
      }
      return prev?.filter((_, i) => i !== index);
    });
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (activeType === 'subcontractor') {
      await handleSaveSubcontractor();
    } else {
      await handleSaveSimple();
    }
  };

  const handleSaveSubcontractor = async () => {
    if (!subFormData?.company_name || !subFormData?.trade) {
      showToast(t('directory.modal.requiredFields') || 'Veuillez remplir tous les champs obligatoires');
      return;
    }
    // Les deux courriels : meme regle que dans OnSiteBid, qui envoie ses
    // demandes de soumission au contact estimation.
    const contactEmail = cleanEmail(subFormData?.email);
    const estimatingEmail = cleanEmail(subFormData?.estEmail);
    const problems = {};
    if (contactEmail && !EMAIL_RE?.test(contactEmail)) problems.email = t('directory.modal.invalidEmail');
    if (estimatingEmail && !EMAIL_RE?.test(estimatingEmail)) problems.estEmail = t('directory.modal.invalidEmail');
    setSubFormErrors(problems);
    if (Object.keys(problems)?.length > 0) return;
    setSaving(true);

    try {
      let payload = {
        companyName: subFormData?.company_name,
        trade: subFormData?.trade,
        contactPerson: subFormData?.contactPerson || null,
        contactEmail: contactEmail || null,
        contactPhone: subFormData?.phone || null,
        address: subFormData?.address || null,
        notes: subFormData?.notes || null,
        rating: subFormData?.rating || null,
        insuranceExpiry: subFormData?.insurance_expiry || null,
        rbqNumber: subFormData?.rbq_number || null,
        rbqExpiry: subFormData?.rbq_expiry || null,
        // Un statut absent ou inconnu n'est pas ecrit tant qu'on n'en choisit pas un.
        status: subFormData?.status || undefined,
        estimatingContactName: subFormData?.estName?.trim() || null,
        estimatingContactEmail: estimatingEmail || null,
        estimatingContactPhone: subFormData?.estPhone?.trim() || null,
        taxId: subFormData?.tax_id?.trim() || null,
      };

      let savedId;
      // Fiche existante, telle qu'enregistree : affichee au retour en lecture.
      let savedRecord = null;

      if (editingRecord) {
        const updated = await subcontractorService?.update(editingRecord?.id, payload);
        savedId = editingRecord?.id;
        // Le service repond en camelCase : converti en snake_case, sinon la
        // ligne garde ses anciennes valeurs (company_name, contact_person…).
        const saved = toSnakeCase(updated);
        const mergeSaved = (r) => ({
          ...r,
          ...saved,
          insurance_doc_url: subFormData?.insurance_doc_url || r?.insurance_doc_url,
          rbq_doc_url: subFormData?.rbq_doc_url || r?.rbq_doc_url,
        });
        savedRecord = mergeSaved(records?.find((r) => r?.id === editingRecord?.id) || editingRecord);
        setRecords((prev) => prev?.map((r) => (r?.id === editingRecord?.id ? mergeSaved(r) : r)));
      } else {
        const created = await subcontractorService?.create(payload);
        savedId = created?.id;

        // Upload pending files for new subcontractor
        const currentOrgId = orgId || (await getCurrentOrgId());
        if (subFormData?._insuranceFile && savedId) {
          try {
            const { path } = await subcontractorService?.uploadDocument(
              currentOrgId, savedId, 'insurance', subFormData?._insuranceFile
            );
            created.insuranceDocUrl = path;
          } catch (err) {
            console.error(err);
          }
        }
        if (subFormData?._rbqFile && savedId) {
          try {
            const { path } = await subcontractorService?.uploadDocument(
              currentOrgId, savedId, 'rbq', subFormData?._rbqFile
            );
            created.rbqDocUrl = path;
          } catch (err) {
            console.error(err);
          }
        }

        setRecords((prev) => [
          {
            id: created?.id,
            company_name: created?.companyName || payload?.companyName,
            trade: created?.trade || payload?.trade,
            contact_person: created?.contactPerson || payload?.contactPerson,
            contact_email: created?.contactEmail || payload?.contactEmail,
            contact_phone: created?.contactPhone || payload?.contactPhone,
            address: created?.address,
            notes: created?.notes,
            rating: created?.rating,
            insurance_expiry: created?.insuranceExpiry || payload?.insuranceExpiry,
            rbq_number: created?.rbqNumber || payload?.rbqNumber,
            rbq_expiry: created?.rbqExpiry || payload?.rbqExpiry,
            insurance_doc_url: created?.insuranceDocUrl,
            rbq_doc_url: created?.rbqDocUrl,
            status: created?.status,
            source: created?.source,
            estimating_contact_name: created?.estimatingContactName,
            estimating_contact_email: created?.estimatingContactEmail,
            estimating_contact_phone: created?.estimatingContactPhone,
            tax_id: created?.taxId,
            created_at: new Date()?.toISOString(),
          },
          ...prev,
        ]);
      }

      // Save additional contacts
      if (savedId) {
        const currentOrgId = orgId || (await getCurrentOrgId());
        for (const contact of additionalContacts) {
          if (contact?._delete && contact?.id) {
            await subcontractorService?.deleteContact(contact?.id);
          } else if (!contact?._delete && contact?.name?.trim()) {
            if (contact?.id) {
              await subcontractorService?.updateContact(contact?.id, {
                role: contact?.role,
                name: contact?.name,
                email: contact?.email,
                phone: contact?.phone,
              });
            } else {
              await subcontractorService?.addContact(savedId, {
                role: contact?.role,
                name: contact?.name,
                email: contact?.email,
                phone: contact?.phone,
              });
            }
          }
        }
      }

      if (savedRecord) {
        // Fiche existante : la fenetre reste ouverte et repasse en lecture.
        // Les contacts sont relus : un contact ajoute recoit ainsi son id et
        // n'est pas cree une 2e fois au prochain enregistrement.
        const contacts = mapContactRows(await subcontractorService?.listContacts(savedId));
        savedContactsRef.current = contacts;
        setAdditionalContacts(contacts);
        setEditingRecord(savedRecord);
        fillFormFromRecord(savedRecord);
        setModalMode('view');
      } else {
        handleCloseModal();
      }
    } catch (err) {
      console.error('[SUB SAVE ERROR]', err);
      showToast("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSimple = async () => {
    if (!formData?.company_name) {
      alert(t('directory.modal.requiredFields'));
      return;
    }
    if (activeType === 'professional' && !formData?.profession) {
      alert(t('directory.modal.requiredFields'));
      return;
    }

    setSaving(true);
    const tableName = getTableName(activeType);

    let payload = {};
    if (activeType === 'client') {
      payload = {
        company_name: formData?.company_name,
        contact_person: formData?.contact_person || null,
        contact_email: formData?.email || null,
        contact_phone: formData?.phone || null,
        address: formData?.address || null,
        notes: formData?.notes || null,
        rating: formData?.rating ?? null,
      };
    } else if (activeType === 'professional') {
      payload = {
        company_name: formData?.company_name,
        profession: formData?.profession,
        contact_person: formData?.contact_person || null,
        contact_email: formData?.email || null,
        contact_phone: formData?.phone || null,
        address: formData?.address || null,
        notes: formData?.notes || null,
        rating: formData?.rating ?? null,
      };
    }

    if (editingRecord) {
      const { data, error } = await supabase
        ?.from(tableName)
        ?.update(payload)
        ?.eq('id', editingRecord?.id)
        ?.select()
        ?.single();
      if (error) {
        console.error(`[DIRECTORY UPDATE ERROR - ${tableName}]`, error);
        setSaving(false);
        return;
      }
      setRecords((prev) => prev?.map((r) => (r?.id === editingRecord?.id ? data : r)));
      // Fiche existante : la fenetre reste ouverte et repasse en lecture.
      setEditingRecord(data);
      fillFormFromRecord(data);
      setModalMode('view');
      setSaving(false);
      return;
    } else {
      const insertPayload = { ...payload, ...(orgId ? { organization_id: orgId } : {}) };
      const { data, error } = await supabase?.from(tableName)?.insert(insertPayload)?.select()?.single();
      if (error) {
        console.error(`[DIRECTORY INSERT ERROR - ${tableName}]`, error);
        setSaving(false);
        return;
      }
      setRecords((prev) => [data, ...prev]);
    }

    setSaving(false);
    setShowAddModal(false);
    setEditingRecord(null);
    setFormData(getInitialFormData(activeType));
  };

  const handleInlineRatingUpdate = async (record, newRating) => {
    const tableName = getTableName(activeType);
    const { data, error } = await supabase
      ?.from(tableName)
      ?.update({ rating: newRating })
      ?.eq('id', record?.id)
      ?.select()
      ?.single();
    if (error) {
      console.error(`[DIRECTORY RATING UPDATE ERROR - ${tableName}]`, error);
      return;
    }
    setRecords((prev) => prev?.map((r) => (r?.id === record?.id ? data : r)));
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubFormChange = (field, value) => {
    setSubFormData((prev) => ({ ...prev, [field]: value }));
    setSubFormErrors((prev) => (prev?.[field] ? { ...prev, [field]: undefined } : prev));
  };

  const toggleDropdown = (e, id) => {
    e?.stopPropagation();
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterCategory('');
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' ? (
      <Icon name="ChevronUp" size={16} className="text-primary" />
    ) : (
      <Icon name="ChevronDown" size={16} className="text-primary" />
    );
  };

  const getAddButtonLabel = () => {
    if (activeType === 'client') return t('directory.addClient');
    if (activeType === 'professional') return t('directory.addProfessional');
    return t('directory.addSubcontractor');
  };

  const getModalTitle = () => {
    if (activeType === 'client') {
      return editingRecord ? t('directory.modal.titleEditClient') : t('directory.modal.titleAddClient');
    }
    if (activeType === 'professional') {
      return editingRecord
        ? t('directory.modal.titleEditProfessional')
        : t('directory.modal.titleAddProfessional');
    }
    return editingRecord ? t('directory.modal.titleEdit') : t('directory.modal.titleAdd');
  };

  // Close dropdown on outside click
  const handlePageClick = () => {
    if (openDropdownId) setOpenDropdownId(null);
  };

  return (
    <div className="pt-0 pb-0 px-0" onClick={handlePageClick}>
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white transition-all ${
            toast?.type === 'error' ? 'bg-red-500' : 'bg-emerald-500'
          }`}
        >
          {toast?.message}
        </div>
      )}
      {/* Header Block */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="Users" size={20} className="text-primary" />
          <h1 className="text-lg font-semibold text-[#2C3E50]">{t('directory.title')}</h1>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs font-normal text-[#6B7280]">{t('directory.subtitle')}</p>
          <button
            onClick={handleAddNew}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-[#FF5A1A] rounded-lg transition-colors shadow-sm"
          >
            <Icon name="Plus" size={13} />
            {getAddButtonLabel()}
          </button>
        </div>
      </div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* Segmented Toggle */}
        <div className="flex items-center h-8 bg-white rounded-lg border border-gray-200 px-1 shadow-sm flex-shrink-0">
          {[
            { key: 'subcontractor', label: t('directory.types.subcontractor') },
            { key: 'client', label: t('directory.types.client') },
            { key: 'professional', label: t('directory.types.professional') },
          ]?.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveType(key)}
              className={`h-6 px-3 text-xs font-medium rounded-md transition-all duration-200 ${
                activeType === key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="h-5 w-px bg-gray-200 flex-shrink-0" />

        {/* Search Input */}
        <input
          type="search"
          placeholder={t('directory.filter.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="h-8 flex-1 min-w-[180px] rounded-lg border border-gray-200 bg-white px-3 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors shadow-sm"
        />

        {activeType === 'subcontractor' && (
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e?.target?.value)}
            className="h-8 rounded-lg border border-gray-200 bg-white px-3 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors shadow-sm flex-shrink-0 cursor-pointer"
          >
            <option value="">{t('directory.filter.allTrades')}</option>
            {(orgTrades?.length > 0 ? orgTrades : [])?.map((trade) => (
              <option key={trade} value={trade}>{trade}</option>
            ))}
          </select>
        )}
        {activeType === 'professional' && (
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e?.target?.value)}
            className="h-8 rounded-lg border border-gray-200 bg-white px-3 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors shadow-sm flex-shrink-0 cursor-pointer"
          >
            <option value="">{t('directory.filter.allProfessions')}</option>
            {PROFESSIONS?.map((prof) => (
              <option key={prof} value={prof}>{prof}</option>
            ))}
          </select>
        )}

        <button
          onClick={handleClearFilters}
          className="h-8 inline-flex items-center gap-1.5 px-2.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
        >
          <Icon name="X" size={13} />
          {t('directory.filter.clearFilters')}
        </button>

        <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
          {t('directory.contactsCount')?.replace('{count}', filteredRecords?.length)}
        </span>
      </div>
      {/* Content Wrapper */}
      <div className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={32} className="animate-spin text-primary" />
          </div>
        ) : filteredRecords?.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-lg border border-gray-200 shadow-sm">
            <Icon name="Users" size={48} className="mx-auto mb-4 text-[#6B7280]" />
            <p className="text-[#6B7280]">{t('directory.empty')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* ── Table Header ── */}
            <div className="flex items-center h-12">
              <div
                className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
                style={{ minWidth: '200px', flex: 1 }}
                onClick={() => handleSort('company_name')}
              >
                <span>{t('directory.table.companyName')}</span>
                <SortIcon columnKey="company_name" />
              </div>

              {/* Trade column — subcontractors only */}
              {activeType === 'subcontractor' && (
                <div
                  className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
                  style={{ width: '150px', flexShrink: 0 }}
                  onClick={() => handleSort('trade')}
                >
                  <span>{t('directory.table.trade')}</span>
                  <SortIcon columnKey="trade" />
                </div>
              )}

              {/* Conformité column — subcontractors only (NEW) */}
              {activeType === 'subcontractor' && (
                <div
                  className="px-6 text-xs font-semibold text-foreground/80"
                  style={{ width: '150px', flexShrink: 0 }}
                >
                  <span>{t('directory.table.compliance') || 'Conformité'}</span>
                </div>
              )}

              {/* Profession column — professionals only */}
              {activeType === 'professional' && (
                <div
                  className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
                  style={{ width: '160px', flexShrink: 0 }}
                  onClick={() => handleSort('profession')}
                >
                  <span>{t('directory.table.profession')}</span>
                  <SortIcon columnKey="profession" />
                </div>
              )}

              <div
                className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
                style={{ width: '170px', flexShrink: 0 }}
                onClick={() => handleSort('contact_person')}
              >
                <span>{t('directory.table.contactPerson')}</span>
                <SortIcon columnKey="contact_person" />
              </div>
              <div
                className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
                style={{ width: '150px', flexShrink: 0 }}
                onClick={() => handleSort('contact_phone')}
              >
                <span>{t('directory.table.phone')}</span>
                <SortIcon columnKey="contact_phone" />
              </div>
              <div
                className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
                style={{ width: '200px', flexShrink: 0 }}
                onClick={() => handleSort('contact_email')}
              >
                <span>{t('directory.table.email')}</span>
                <SortIcon columnKey="contact_email" />
              </div>
              <div
                className="px-6 text-xs font-semibold text-foreground/80"
                style={{ width: '130px', flexShrink: 0 }}
              >
                <span>{t('directory.table.rating')}</span>
              </div>
              <div
                className="px-6 text-xs font-semibold text-foreground/80 text-right"
                style={{ width: '90px', flexShrink: 0 }}
              >
                <span>{t('directory.table.actions')}</span>
              </div>
            </div>

            {/* ── Row Cards ── */}
            {sortedRecords?.map((record) => {
              // Subcontractor-specific expiry logic
              const urgentExpiry =
                activeType === 'subcontractor'
                  ? getMostUrgentExpiry(record?.insurance_expiry, record?.rbq_expiry)
                  : null;
              const insStatus = activeType === 'subcontractor' ? getExpiryStatus(record?.insurance_expiry) : null;
              const rbqStatus = activeType === 'subcontractor' ? getExpiryStatus(record?.rbq_expiry) : null;
              const showAlert =
                activeType === 'subcontractor' &&
                ((insStatus?.days !== null && insStatus?.days <= 7) ||
                  (rbqStatus?.days !== null && rbqStatus?.days <= 7));
              const tooltipText = [
                record?.insurance_expiry ? `Assurance: ${record?.insurance_expiry}` : null,
                record?.rbq_expiry ? `RBQ: ${record?.rbq_expiry}` : null,
              ]
                ?.filter(Boolean)
                ?.join(' | ');

              return (
                <div
                  key={record?.id}
                  onClick={() => handleView(record)}
                  className="flex items-center h-10 bg-white border border-gray-200 rounded-lg shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_0_rgba(0,0,0,0.02)] transition-all duration-200 cursor-pointer hover:bg-gray-100/60 hover:border-primary"
                >
                  {/* Company name + alert icon */}
                  <div
                    className="px-6 text-xs leading-none text-foreground min-w-0 truncate whitespace-nowrap overflow-hidden text-ellipsis flex items-center gap-1.5"
                    style={{ minWidth: '200px', flex: 1 }}
                  >
                    <span className="truncate">{record?.company_name}</span>
                    {showAlert && (
                      <span title={tooltipText} className="flex-shrink-0">
                        <Icon name="AlertTriangle" size={13} className="text-orange-500" />
                      </span>
                    )}
                  </div>

                  {/* Trade — subcontractors */}
                  {activeType === 'subcontractor' && (
                    <div className="px-6" style={{ width: '150px', flexShrink: 0 }}>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {record?.trade}
                      </span>
                    </div>
                  )}

                  {/* Conformité pill — subcontractors only (NEW) */}
                  {activeType === 'subcontractor' && (
                    <div className="px-6" style={{ width: '150px', flexShrink: 0 }}>
                      {urgentExpiry ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${urgentExpiry?.colorClass}`}
                        >
                          {urgentExpiry?.label}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  )}

                  {/* Profession — professionals */}
                  {activeType === 'professional' && (
                    <div className="px-6 text-xs leading-none text-foreground" style={{ width: '160px', flexShrink: 0 }}>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {record?.profession}
                      </span>
                    </div>
                  )}

                  <div className="px-6 text-xs leading-none text-foreground" style={{ width: '170px', flexShrink: 0 }}>
                    {record?.contact_person || '-'}
                  </div>
                  <div className="px-6 text-xs leading-none text-foreground" style={{ width: '150px', flexShrink: 0 }}>
                    {record?.contact_phone || '-'}
                  </div>
                  <div className="px-6 text-xs leading-none text-foreground" style={{ width: '200px', flexShrink: 0 }}>
                    {record?.contact_email || '-'}
                  </div>
                  <div className="px-6" style={{ width: '130px', flexShrink: 0 }} onClick={(e) => e?.stopPropagation()}>
                    <StarRating
                      value={record?.rating ?? null}
                      onChange={(newRating) => handleInlineRatingUpdate(record, newRating)}
                      size="sm"
                    />
                  </div>
                  <div className="px-6 text-right relative" style={{ width: '90px', flexShrink: 0 }} onClick={(e) => e?.stopPropagation()}>
                    <button
                      onClick={(e) => toggleDropdown(e, record?.id)}
                      className="text-gray-600 hover:text-primary text-lg font-bold leading-none p-1"
                    >
                      ⋮
                    </button>
                    {openDropdownId === record?.id && (
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-sm py-1 text-sm z-50 min-w-[120px]">
                        <button
                          onClick={(e) => handleEdit(e, record)}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700"
                        >
                          {t('directory.dropdown.edit')}
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(e, record?.id)}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700"
                        >
                          {t('directory.dropdown.delete')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* ══════════════════════════════════════════════════════════════════════
          Add/Edit Modal — branches on activeType
          - Sous-traitants → fiche SubcontractorCard (la meme que dans OnSiteBid)
          - Clients / Professionnels → EXISTING simple modal (unchanged)
      ══════════════════════════════════════════════════════════════════════ */}
      {showAddModal && (
        <>
          {/* ── Fiche sous-traitant : lecture, edition et ajout ── */}
          {activeType === 'subcontractor' && (
            <SubcontractorCard
              record={editingRecord}
              isView={isView}
              form={subFormData}
              errors={subFormErrors}
              saving={saving}
              onChange={handleSubFormChange}
              trades={orgTrades}
              tradeLabels={tradeLabels}
              rbqSignedUrl={rbqSignedUrl}
              insuranceSignedUrl={insuranceSignedUrl}
              uploadingRbq={uploadingRbq}
              uploadingInsurance={uploadingInsurance}
              onDocUpload={handleDocUpload}
              onDocRemove={handleDocRemove}
              contacts={additionalContacts}
              onAddContact={addContactRow}
              onUpdateContact={updateContactRow}
              onRemoveContact={removeContactRow}
              onClose={handleCloseModal}
              onEdit={() => setModalMode('edit')}
              onCancel={editingRecord ? handleCancelEdit : handleCloseModal}
              onSave={handleSave}
            />
          )}

          {/* ── EXISTING simple modal — Clients & Professionnels (unchanged) ── */}
          {(activeType === 'client' || activeType === 'professional') && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={(e) => {
                if (isView && e?.target === e?.currentTarget) handleCloseModal();
              }}
            >
              <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-[#2C3E50]">{isView ? editingRecord?.company_name : getModalTitle()}</h2>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 hover:bg-muted rounded-md transition-150"
                  >
                    <Icon name="X" size={20} className="text-muted-foreground" />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('directory.modal.companyName')} {!isView && <span className="text-red-500">*</span>}
                    </label>
                    {isView ? (
                      <ReadValue value={formData?.company_name} className="text-sm text-foreground" />
                    ) : (
                      <input
                        type="text"
                        value={formData?.company_name}
                        onChange={(e) => handleFormChange('company_name', e?.target?.value)}
                        className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        placeholder={t('directory.modal.companyNamePlaceholder')}
                      />
                    )}
                  </div>

                  {/* Profession — professionals only */}
                  {activeType === 'professional' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        {t('directory.modal.profession')} {!isView && <span className="text-red-500">*</span>}
                      </label>
                      {isView ? (
                        <ReadValue value={formData?.profession} className="text-sm text-foreground" />
                      ) : (
                        <select
                          value={formData?.profession}
                          onChange={(e) => handleFormChange('profession', e?.target?.value)}
                          className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        >
                          <option value="">{t('directory.modal.professionPlaceholder')}</option>
                          {PROFESSIONS?.map((prof) => (
                            <option key={prof} value={prof}>{prof}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Contact Person */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('directory.modal.contactPerson')}
                    </label>
                    {isView ? (
                      <ReadValue value={formData?.contact_person} className="text-sm text-foreground" />
                    ) : (
                      <input
                        type="text"
                        value={formData?.contact_person}
                        onChange={(e) => handleFormChange('contact_person', e?.target?.value)}
                        className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        placeholder={t('directory.modal.contactPersonPlaceholder')}
                      />
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('directory.modal.phone')}
                    </label>
                    {isView ? (
                      <ReadValue value={formData?.phone} className="text-sm text-foreground" />
                    ) : (
                      <input
                        type="tel"
                        value={formData?.phone}
                        onChange={(e) => handleFormChange('phone', e?.target?.value)}
                        className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        placeholder={t('directory.modal.phonePlaceholder')}
                      />
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('directory.modal.email')}
                    </label>
                    {isView ? (
                      <ReadValue value={formData?.email} className="text-sm text-foreground" />
                    ) : (
                      <input
                        type="email"
                        value={formData?.email}
                        onChange={(e) => handleFormChange('email', e?.target?.value)}
                        className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        placeholder={t('directory.modal.emailPlaceholder')}
                      />
                    )}
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('directory.modal.address')}
                    </label>
                    {isView ? (
                      <ReadValue value={formData?.address} className="text-sm text-foreground" />
                    ) : (
                      <input
                        type="text"
                        value={formData?.address}
                        onChange={(e) => handleFormChange('address', e?.target?.value)}
                        className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        placeholder={t('directory.modal.addressPlaceholder')}
                      />
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('directory.modal.notes')}
                    </label>
                    {isView ? (
                      <ReadValue value={formData?.notes} multiline className="text-sm text-foreground" />
                    ) : (
                      <textarea
                        value={formData?.notes}
                        onChange={(e) => handleFormChange('notes', e?.target?.value)}
                        rows={3}
                        className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                        placeholder={t('directory.modal.notesPlaceholder')}
                      />
                    )}
                  </div>

                  {/* Rating */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('directory.modal.rating')}
                    </label>
                    <StarRating
                      value={formData?.rating ?? null}
                      onChange={isView ? undefined : (val) => handleFormChange('rating', val)}
                      size="lg"
                    />
                  </div>
                </div>
                {isView ? (
                  <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center justify-end space-x-3">
                    <Button variant="outline" onClick={handleCloseModal}>
                      {t('directory.modal.close')}
                    </Button>
                    <Button onClick={() => setModalMode('edit')}>{t('directory.modal.edit')}</Button>
                  </div>
                ) : (
                  <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center justify-end space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => (editingRecord ? handleCancelEdit() : setShowAddModal(false))}
                      disabled={saving}
                    >
                      {t('directory.modal.cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? (
                        <span className="flex items-center space-x-2">
                          <Icon name="Loader2" size={14} className="animate-spin" />
                          <span>{t('directory.modal.saving')}</span>
                        </span>
                      ) : (
                        <>{editingRecord ? t('directory.modal.save') : t('directory.modal.add')}</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-lg max-w-sm w-full p-6">
            <h3 className="text-base font-semibold text-[#2C3E50] mb-2">
              {t('directory.deleteModal.title')}
            </h3>
            <p className="text-sm text-gray-600 mb-6">{t('directory.deleteModal.message')}</p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('directory.deleteModal.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                {t('directory.deleteModal.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Directory;
